import { UseFormReset } from "react-hook-form";
import { HTTP_STATUSES } from "@miplatform/constants";
import { arrayMove } from "@dnd-kit/sortable";
import { useCategories, useDropTypes, usePriceLevels } from "@mibuildweb/hooks";
import {
  ICategoryDropPayload,
  IPriceLevelPayload,
} from "./PricingMatrix.types";

export const usePricingMatrixViewModel = ({
  reset = () => {},
}: usePricingMatrixViewModelProps) => {
  const {
    isLoading: isCategoriesLoading,
    categories,
    categoryDrops,
    getCategories,
    getCategoryDrops,
    createCategoryDrop,
    deleteCategoryDrop,
  } = useCategories();
  const {
    isLoading: isDropTypesLoading,
    dropTypes,
    getDropTypes,
  } = useDropTypes();
  const {
    isLoading: isPriceLevelLoading,
    priceLevels,
    getPriceLevels,
    createPriceLevel,
    updatePriceLevel,
    reorderPriceLevels,
    duplicatePriceLevel,
    deletePriceLevel,
  } = usePriceLevels();

  const handleCreateCategoryDrop = async (
    drops: string[],
    categoryId: string
  ) => {
    const promises = drops.map(async (dropId) => {
      const dropType = dropTypes.find((dt) => dt.id === dropId);
      if (!dropType) return false;
      const data = { name: dropType.name, dropTypeId: dropType.id };
      const response = await createCategoryDrop(data, categoryId);
      return response.status === HTTP_STATUSES.OK;
    });
    const results = await Promise.all(promises);
    if (results.some((success) => success)) {
      getCategoryDrops();
    }
  };

  const handleDeleteCategoryDrop = async (
    categoryId: string,
    data: Partial<ICategoryDropPayload>
  ) => {
    const response = await deleteCategoryDrop(categoryId, data);
    if (response.status === HTTP_STATUSES.OK) {
      getCategoryDrops();
    }
  };

  const handleCreatePriceLevel = async (data: IPriceLevelPayload) => {
    const modifiedData = { ...data, displayOrder: priceLevels.length };
    const response = await createPriceLevel(modifiedData);
    if (response.status === HTTP_STATUSES.OK) {
      const dropPrices = categoryDrops.map((cd) => ({
        dropTypeId: cd.dropTypeId,
        categoryId: cd.categoryId,
        dropId: cd.id,
      }));
      reset({ ...response.data, dropPrices });
      getPriceLevels();
    }
  };

  const handleUpdatePriceLevel = async (
    data: Partial<IPriceLevelPayload>,
    successText?: string
  ) => {
    const modifiedData = {
      ...data,
      dropPrices: data.dropPrices?.filter((dp) => dp.dirty),
    };
    const response = await updatePriceLevel(modifiedData, successText);
    if (response.status === HTTP_STATUSES.OK) {
      getPriceLevels();
    }
  };

  const handleReorderPriceLevels = async (
    direction: "left" | "right",
    idx: number
  ) => {
    const to = direction === "left" ? idx - 1 : idx + 1;
    const reorderedLevels = arrayMove(priceLevels, idx, to);
    const data = reorderedLevels.map((level, index) => ({
      id: level.id,
      displayOrder: index,
    }));
    const response = await reorderPriceLevels(data);
    if (response.status === HTTP_STATUSES.OK) {
      getPriceLevels();
    }
  };

  const handleDuplicatePriceLevel = async (
    data: Partial<IPriceLevelPayload>
  ) => {
    const response = await duplicatePriceLevel(data);
    if (response.status === HTTP_STATUSES.OK) {
      getPriceLevels();
    }
  };

  const handleDeletePriceLevel = async (data: Partial<IPriceLevelPayload>) => {
    const response = await deletePriceLevel(data);
    if (response.status === HTTP_STATUSES.OK) {
      getPriceLevels();
    }
  };

  return {
    isLoading: isCategoriesLoading || isDropTypesLoading || isPriceLevelLoading,
    categories,
    dropTypes,
    categoryDrops,
    priceLevels,
    getCategories,
    getDropTypes,
    getCategoryDrops,
    getPriceLevels,
    handleCreateCategoryDrop,
    handleDeleteCategoryDrop,
    handleCreatePriceLevel,
    handleUpdatePriceLevel,
    handleReorderPriceLevels,
    handleDuplicatePriceLevel,
    handleDeletePriceLevel,
  };
};

interface usePricingMatrixViewModelProps {
  reset?: UseFormReset<IPriceLevelPayload>;
}
